import { useMemo } from 'react';
import { IParsedChartDataRecord, IDisplayedMeasure } from '../interfaces';
import { isNil } from 'lodash';
import { computeMeasureValue } from '../../../utils/formatting';

interface IProps {
  chartData: IParsedChartDataRecord[];
  displayedMeasures: IDisplayedMeasure[];
  dataKeysOnMeasure: Record<string, string[]>;
}

/**
 * If calculation is changed on specific measure,
 * we need to calculate all data to make sure it display correctly on rechart.
 * Since calculation feature is only available in simple measure (only 1 measure totally) expression.
 * it won't take too much time to finish the calculation.
 */
export default ({
  chartData,
  displayedMeasures,
  dataKeysOnMeasure,
}: IProps): IParsedChartDataRecord[] => {
  return useMemo(() => {
    const formattedMeasures = displayedMeasures.filter(
      (displayedMeasure) =>
        displayedMeasure.format?.calculationValue !== undefined &&
        displayedMeasure.format?.calculationUnit &&
        displayedMeasure.isVisible,
    );
    // if no visible measure with format.calculation, return raw data.
    if (formattedMeasures.length === 0 || chartData.length === 0) {
      return chartData;
    }

    // it's O(n^2) because of the length of visible measure key is max to 1 right now.
    // iterate each day in chart data and manually calculate the formatted value based opn keyDictionary Object
    const res = chartData.map((singleDayData) => {
      const singleDayDataCopy = { ...singleDayData };
      for (const formattedMeasure of formattedMeasures) {
        if (!dataKeysOnMeasure[formattedMeasure.key]) {
          continue;
        }
        for (const dataKey of dataKeysOnMeasure[formattedMeasure.key]) {
          const value = singleDayData[dataKey];
          if (
            !isNil(value) &&
            typeof value === 'number' &&
            formattedMeasure.format
          ) {
            const { calculationValue, calculationUnit } =
              formattedMeasure.format;
            singleDayDataCopy[dataKey] = computeMeasureValue(
              value,
              calculationUnit,
              calculationValue,
            );
          }
        }
      }
      return singleDayDataCopy;
    });

    return res;
  }, [chartData, displayedMeasures, dataKeysOnMeasure]);
};
